import { makeAutoObservable, runInAction } from "mobx";
import { IMerchantOnboarding } from "../onboarding-types";
import OnboardingDataStore, {
    IGetMerchantOnboardingModel,
    IOnboardingScreenStore
} from "./onboarding-data-store";
import OnboardingService from "../../../services/api/onboarding-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import {ONBOARDING_STEPS, PLAN_TYPE, RESTOCK_LOCATION_TAB, RETURN_LOCATION_TAB} from "../constants";
import { OnboardingNavigationStore } from "./onboarding-navigation-store";
import { IIframeService } from "../../../services/iframe-service/iframe-service";
import onboardingMixPanelStore, {ONBOARDING_MIXPANEL_EVENT} from "./onboarding-mixpanel-store";
import {DROP_OFF_LOCATION} from "../../return-methods/constants";
import {RESOLUTION_ID} from "../../resolutions/constants";
import OnboardingPlansStores from "./onboarding-plans-stores";
import {PlanStore} from "../../subscription-plans/stores/subscription-plans-store";
import walletStore from "../../billing/stores/wallet/wallet-store";

class FinalScreenStore implements IOnboardingScreenStore {
    private _getModel: IGetMerchantOnboardingModel;
    private _onboardingDataStore: OnboardingDataStore;
    private readonly _stepName: ONBOARDING_STEPS = ONBOARDING_STEPS.FINAL;
    private _isLoading: boolean = false;
    private _isCompleted: boolean = false;

    constructor(onboardingDataStore: OnboardingDataStore, private _navigationStore: OnboardingNavigationStore) {
        this._onboardingDataStore = onboardingDataStore;
        this._getModel = this._onboardingDataStore.getModel;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get onboardingService(): OnboardingService {
        return rootContainer.get<OnboardingService>(TYPE.OnboardingService);
    }

    private get iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    public isComplete(): boolean {
        return !!this._getModel()?.get_started_clicked;
    }

    public getUpdatedModel(): IMerchantOnboarding {
        return { ...this._getModel() } as IMerchantOnboarding;
    }

    public hasChanges(): boolean {
        throw new Error('not implemented');
    }

    public getMixPanelData(): Record<string, any> {
        const updateModel = this.getUpdatedModel();
        let planData = {};

        if(updateModel.plan_id) {
            const plan = OnboardingPlansStores.getPlanById(updateModel.plan_id) as PlanStore;
            planData = {
                selected_plan: {
                    id: plan.id,
                    plan_name: plan.name,
                },
                plan_type: plan.isDefaultCoveragePlan ? PLAN_TYPE.COVERAGE : PLAN_TYPE.DEFAULT
            }
        }

        return {
            ...updateModel,
            return_address_type: updateModel.address?.shopify_location_id ? RETURN_LOCATION_TAB.PLATFORM : RETURN_LOCATION_TAB.CUSTOM_ADDRESS,
            restocking_location_type: updateModel.shopify_restock_location_id === DROP_OFF_LOCATION.FULFILLMENT_LOCATION ? RESTOCK_LOCATION_TAB.FULFILLMENT_LOCATION : RESTOCK_LOCATION_TAB.PLATFORM,
            using_same_address_for_restock_address: updateModel.address?.shopify_location_id === updateModel.shopify_restock_location_id,
            merchant_onboarding_resolutions: updateModel.merchant_onboarding_resolutions.map((merchantOnboardingResolution) =>
                Object.keys(RESOLUTION_ID).find(key => RESOLUTION_ID[key as keyof typeof RESOLUTION_ID] === merchantOnboardingResolution.resolution_id)
            ),
            wallet_configured: walletStore.hasPaymentMethod,
            wallet_minimum_balance_amount: walletStore.rechargeThreshold,
            wallet_recharge_amount: walletStore.rechargeAmount,
            ...planData
        };
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    public discardChanges(): void {}

    public async completeOnboarding(): Promise<void> {
        if (this._isLoading || this._isCompleted) {
            /** prevent clicking calling processor while in progress or already completed */
            return;
        }

        this._isLoading = true;

        try {
            /** updating get_started_clicked field on onboarding record inside controller's method that runs the processor */
            const updatedModelWithReport = await this.onboardingService.runProcessor(this.getUpdatedModel());
            runInAction(() => {
                this._isCompleted = true;
            });

            onboardingMixPanelStore.trackEvent(ONBOARDING_MIXPANEL_EVENT.ONBOARDING_COMPLETED, {
                from: this._stepName,
                collected_data: {
                    ...this.getMixPanelData(),
                    get_started_clicked: updatedModelWithReport.onboarding?.get_started_clicked,
                    get_started_clicked_at: updatedModelWithReport.onboarding?.get_started_clicked_at
                }
            });
            onboardingMixPanelStore.stopMixpanelSessionRecording();

            this.iframeService.closeOnboardingWizardModal();
            this.iframeService.refreshParentFrame();

            /** TODO:
             * 1. while loading prevent the popup from closing + show loaded on the button
             * 2. is success - close the popup
             * 3. if failed - disable button and enable closing the popup
             */

        } catch (error) {
            onboardingMixPanelStore.trackEvent(ONBOARDING_MIXPANEL_EVENT.ONBOARDING_ATTEMPT_FAILED,
                {
                    from: this._stepName,
                    error
                }
            );
        } finally {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    public previousStep(): void {
        this._navigationStore.previousStep();
    }
}

export default FinalScreenStore;